import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  GridApiPremium,
  GridFilterModel,
  GridSortModel,
  GridColumnVisibilityModel,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRowSelectionModel,
  GridPinnedColumnFields,
} from '@mui/x-data-grid-premium';
import Actions from '../../store/Actions';
import { showSnackbar } from '../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../context/Snackbar/Types';
import { PortfolioDataTable } from '../../types/generatedTypes';
import { ReportParams } from './types';
import { useGroups } from '../../hooks/useGroups';
import { GroupedRowData } from '../../components/Groups/types';
import { useGridSelectionHandler } from '../../components/Groups/useGridSelection';
import { useExpandGroups } from '../../components/Groups/groupUtils';

interface UsePortfolioDataProps {
  apiRef: React.RefObject<GridApiPremium | null>;
  syncPortfolioCustodianFromIllio: (
    refreshCallback: () => Promise<void>
  ) => Promise<void>;
  displayDataRef: React.RefObject<GroupedRowData[]>;
  displayData: GroupedRowData[];
}

export const usePortfolioData = ({
  apiRef,
  syncPortfolioCustodianFromIllio,
  displayDataRef,
  displayData,
}: UsePortfolioDataProps) => {
  const [portfolioData, setPortfolioData] = useState<PortfolioDataTable[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [autosizeColumns, setAutosizeColumns] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  // Groups functionality
  const {
    groups,
    setGroups,
    isGroupingEnabled,
    setIsGroupingEnabled,
    isGroupsModalOpen,
    loadGroupsFromBackend,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
  } = useGroups({ pageType: 'portfolio' });

  // Column fields configuration
  const columnFields = useMemo(
    () => ['client_name', 'portfolio_name', 'portfolio_id', 'leading_currency'],
    []
  );

  // Handle autosize state changes
  useEffect(() => {
    if (autosizeColumns && apiRef.current) {
      // Get actual visible columns from the DataGrid API
      const allColumns = apiRef.current.getAllColumns();

      // Filter out internal grid columns that shouldn't be auto-sized
      const internalColumns = [
        '__check__', // Checkbox column
        '__tree_data_group__', // Tree data grouping column
      ];

      const visibleUserColumns = allColumns
        .filter(col => !internalColumns.includes(col.field))
        .map(col => col.field);

      if (visibleUserColumns.length > 0) {
        apiRef.current.autosizeColumns({
          includeHeaders: true,
          includeOutliers: true,
          expand: true,
          columns: visibleUserColumns,
        });
      }
    } else if (!autosizeColumns && apiRef.current) {
      // Get actual visible columns and reset their widths
      const allColumns = apiRef.current.getAllColumns();
      const internalColumns = ['__check__', '__tree_data_group__'];

      allColumns.forEach(col => {
        if (!internalColumns.includes(col.field)) {
          apiRef.current?.setColumnWidth(col.field, 0);
        }
      });
    }
  }, [autosizeColumns, apiRef, isGroupingEnabled]); // Added isGroupingEnabled to dependencies

  const fetchPortfolioData = useCallback(async () => {
    try {
      setLoading(true);
      const portfolioData = await Actions.getPortfolioData();

      // Map PortfolioData to PortfolioDataTable (add _id field)
      const tableData: PortfolioDataTable[] = portfolioData.map(p => ({
        ...p,
        _id: p.id, // Add _id for DataGrid
      }));

      // Remove duplicates based on portfolio_id
      const uniqueData = tableData.filter(
        (portfolio, index, self) =>
          index ===
          self.findIndex(p => p.portfolio_id === portfolio.portfolio_id)
      );

      setPortfolioData(uniqueData);
    } catch (error) {
      showSnackbar({
        title: 'Error',
        message: 'Failed to load portfolio data',
        type: SnackbarType.error,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Create expandGroups wrapper with current context
  const expandGroups = useExpandGroups(groups, displayData);

  // Row selection handler using shared utility
  const handleRowSelectionChange = useGridSelectionHandler(
    selectedRows,
    setSelectedRows,
    displayData,
    {
      getRowId: row => (row._id || row.id).toString(),
      expandGroups,
    }
  );

  // Report modal handlers
  const handleOpenReportModal = useCallback(() => {
    setIsReportModalOpen(true);
  }, []);

  const handleCloseReportModal = useCallback(() => {
    setIsReportModalOpen(false);
  }, []);

  // Prepare available rows for groups modal
  const availableRowsForGroups = useMemo(
    () =>
      portfolioData.map(row => ({
        id: row._id,
        label: `${row.client_name} - ${row.portfolio_name}`,
        ...row,
      })),
    [portfolioData]
  );

  const handleSubmitReport = useCallback(
    async (params: ReportParams) => {
      try {
        setIsGeneratingReport(true);

        // Get selected portfolio data
        const selectedPortfolios = portfolioData.filter(portfolio =>
          selectedRows.includes(portfolio._id)
        );

        // Map selected portfolios to the required format, filtering out invalid data
        const portfoliosForReport = selectedPortfolios
          .filter(
            portfolio => portfolio.portfolio_id && portfolio.portfolio_name
          )
          .map(portfolio => ({
            portfolioId: portfolio.portfolio_id!,
            name: portfolio.portfolio_name!,
            inceptionDate: portfolio.inception_date || null,
            clientName: portfolio.client_name || null,
          }));

        // Check if we have valid portfolios for the report
        if (portfoliosForReport.length === 0) {
          showSnackbar({
            title: 'Warning',
            message:
              'Selected portfolios are missing required data (ID or name). Cannot generate report.',
            type: SnackbarType.warning,
          });
          return;
        }

        // Call the unified generate report API with all parameters
        const result = await Actions.generateReport({
          reportType: params.reportType,
          portfolios: portfoliosForReport,
          fromDate: params.fromDate,
          toDate: params.toDate,
          isInception: params.isInception,
          filenameTemplate: params.filenameTemplate,
        });

        // Show appropriate message based on metadata
        const { metadata } = result;

        if (!metadata) {
          // No metadata - show warning per project rules (no fallbacks)
          showSnackbar({
            title: 'Warning',
            message:
              'Report downloaded but metadata missing. Check console for details.',
            type: SnackbarType.warning,
          });
          console.error('Missing metadata in report generation response');
          setIsReportModalOpen(false);
          return;
        }

        const { successCount, failedCount, totalCount, failedPortfolios } =
          metadata;

        if (failedCount === 0) {
          // All succeeded
          showSnackbar({
            title: 'Success',
            message: `Report generated successfully for ${successCount} portfolio${successCount > 1 ? 's' : ''}`,
            type: SnackbarType.success,
          });
        } else if (successCount > 0) {
          // Partial success
          const failedNames = failedPortfolios.map(fp => fp.name).join(', ');
          console.error('Failed portfolios:', failedPortfolios);

          showSnackbar({
            title: 'Partial Success',
            message: `Report generated for ${successCount} out of ${totalCount} portfolios. ${failedCount} failed: ${failedNames}`,
            type: SnackbarType.warning,
          });
        } else {
          // All failed
          const failedNames = failedPortfolios.map(fp => fp.name).join(', ');
          console.error('All portfolios failed:', failedPortfolios);

          showSnackbar({
            title: 'Error',
            message: `Failed to generate reports for all ${totalCount} portfolios: ${failedNames}`,
            type: SnackbarType.error,
          });
        }

        setIsReportModalOpen(false);
      } catch (error) {
        showSnackbar({
          title: 'Error',
          message: 'An error occurred while generating the report',
          type: SnackbarType.error,
        });
      } finally {
        setIsGeneratingReport(false);
      }
    },
    [portfolioData, selectedRows]
  );

  // Export data handler
  const handleExport = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.exportDataAsCsv();
    }
  }, [apiRef]);

  // Sync handler wrapper
  const handleSyncPortfolioCustodianFromIllio = useCallback(async () => {
    await syncPortfolioCustodianFromIllio(fetchPortfolioData);
  }, [syncPortfolioCustodianFromIllio, fetchPortfolioData]);

  // Apply grid state functions
  const applyColumnFilters = useCallback(
    (filters: GridFilterModel) => {
      if (apiRef.current && filters) {
        apiRef.current.setFilterModel(filters);
      }
    },
    [apiRef]
  );

  const applySortModel = useCallback(
    (sortModel: GridSortModel) => {
      if (apiRef.current && sortModel) {
        apiRef.current.setSortModel(sortModel);
      }
    },
    [apiRef]
  );

  const applyColumnVisibility = useCallback(
    (visibilityModel: GridColumnVisibilityModel) => {
      if (apiRef.current) {
        apiRef.current.setColumnVisibilityModel(visibilityModel);
      }
      setColumnVisibilityModel(visibilityModel);
    },
    [apiRef]
  );

  const applyPinnedColumns = useCallback(
    (pinnedColumns: GridPinnedColumnFields) => {
      if (!apiRef.current) return;
      try {
        // Ensure checkbox column is always first in left pinned columns
        const modifiedPinnedColumns: GridPinnedColumnFields = {
          ...pinnedColumns,
          left: pinnedColumns.left
            ? [
                GRID_CHECKBOX_SELECTION_COL_DEF.field,
                ...pinnedColumns.left.filter(
                  col => col !== GRID_CHECKBOX_SELECTION_COL_DEF.field
                ),
              ]
            : [GRID_CHECKBOX_SELECTION_COL_DEF.field],
        };

        if (typeof apiRef.current.setPinnedColumns === 'function') {
          apiRef.current.setPinnedColumns(modifiedPinnedColumns);
        }
      } catch (error) {
        // Silent catch - pinned columns are optional feature
      }
    },
    [apiRef]
  );

  return {
    portfolioData,
    loading,
    selectedRows,
    isReportModalOpen,
    isGeneratingReport,
    autosizeColumns,
    setAutosizeColumns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnFields,
    fetchPortfolioData,
    handleRowSelectionChange,
    handleOpenReportModal,
    handleCloseReportModal,
    handleSubmitReport,
    handleExport,
    handleSyncPortfolioCustodianFromIllio,
    applyColumnFilters,
    applySortModel,
    applyColumnVisibility,
    applyPinnedColumns,
    expandGroups,
    // Groups
    groups,
    setGroups,
    loadGroupsFromBackend,
    isGroupsModalOpen,
    isGroupingEnabled,
    setIsGroupingEnabled,
    availableRowsForGroups,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
  };
};
